import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/trailer-cover.jpg";
import heroPicture from "@/assets/trailer-cover.jpg?w=480;640;800;1024;1600;1920&format=avif;webp;jpeg&quality=55&as=picture";
import livestockBlack from "@/assets/trailer-livestock-black.jpg";
import redGooseneck from "@/assets/trailer-red-gooseneck.jpg";
import redGooseneckInstalled from "@/assets/trailer-red-gooseneck-installed.jpg";
import blueStockTanTarp from "@/assets/blue-stock-trailer-tan-tarp.jpg";
import tanRanchTrailer from "@/assets/tan-stock-trailer-cover-texas-ranch.jpg";
import { LetsTalkForm } from "@/components/LetsTalkForm";

const featuredTanRanch = (
  <section className="border-t-4 border-navy-deep bg-cream py-16">
    <div className="mx-auto max-w-7xl px-4 lg:px-8">
      <figure className="sm:float-left sm:mr-6 sm:mb-3 sm:w-[48%] md:w-[44%] lg:w-[40%]">
        <img
          src={tanRanchTrailer}
          alt="Custom tan heavy-duty vinyl stock trailer cover by Texas Made Shade installed on a gooseneck livestock trailer parked on a Texas Hill Country ranch"
          width={1200}
          height={900}
          className="block h-auto w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
          Tan vinyl stock trailer top · Texas Hill Country ranch
        </figcaption>
      </figure>
      <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Build</div>
      <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
        Tan Vinyl Stock Trailer Top — Patterned for the Ranch
      </h2>
      <p className="mt-4 text-lg text-navy-deep/80">
        This long-bed gooseneck stock trailer was patterned in our Seguin shop and fitted with a custom tan
        heavy-duty vinyl-coated polyester top. Tan reads soft against Texas dust and reflects sun off the cattle
        below — a working-ranch favorite for hauling between pasture, sale barn and lease.
      </p>
      <p className="mt-4 text-lg text-navy-deep/80">
        Reinforced edges, brass grommets and shock-cord lacing tie the top tight to the rail so it holds steady at
        highway speed. Every cover is sewn to the exact frame of your trailer — W-W, Calico, Featherlite, GR or
        custom — with hardware that handles daily ranch use across South and Central Texas.
      </p>
      <div className="clear-both" />
    </div>
  </section>
);

export const Route = createFileRoute("/trailer-covers")({
  head: (ctx) => ({
      links: [
        canonicalLink(pathFromMatch(ctx)),
        {
          rel: "preload",
          as: "image",
          type: "image/avif",
          imageSrcSet: heroPicture.sources.avif,
          imageSizes: "100vw",
          fetchPriority: "high",
        },
      ],
      meta: routeMeta(ctx, {
        title: "Custom Stock Trailer Covers & Ag Tarps in Texas | Texas Made Shade",
        description: "Custom-built stock trailer covers, livestock trailer tarps, gooseneck tops, and ranch equipment covers. Heavy 18oz vinyl, patterned to your trailer.",
        image: hero,
        socialTitle: "Custom Stock Trailer Covers & Ag Tarps — Texas Made Shade & Covers",
        socialDescription: "Heavy-duty stock trailer covers, livestock tarps, and ranch equipment shrouds — patterned to fit any trailer. Built tough in Seguin, Texas.",
      }),
    scripts: serviceHeadScripts({
      path: "/trailer-covers",
      serviceName: "Trailer Covers",
      breadcrumbName: "Trailer Covers",
      description: "Custom canvas and vinyl trailer covers — stock trailers, hay trailers, equipment and gooseneck covers built for Texas weather.",
    }),
    }),
  component: () => (
    <ServicePage
      currentPath="/trailer-covers"
      serviceName="Trailer Covers"
      eyebrow="★ AG TARPS / STOCK TRAILER ★"
      title="Ag Tarps, Stock Trailer Covers & Ranch Canvas"
      intro="Custom-built covers for livestock trailers, stock trailers, and ranch equipment. Patterned to your trailer, built to take a beating."
      heroImage={heroPicture}
      hideLetsTalkForm
      preContent={<><LetsTalkForm />{featuredTanRanch}</>}
      heroCta={{
        eyebrow: "Free Quote",
        headline: "Get a Free Quote on Your Custom Stock Trailer Cover",
        subhead: "Tell us about your trailer or ranch equipment — we'll respond with sizing questions and a no-pressure estimate, usually same day.",
        primaryHref: "#lets-talk",
        primaryLabel: "Get a Free Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Heavy 18oz vinyl-coated polyester",
        "Reinforced stress points and double-stitched seams",
        "Roll-up sides and end gates available",
        "Custom-fit to any make of stock trailer",
        "Replacement tarps for existing frames",
        "Ranch and ag equipment covers",
      ]}
      body={[
        { heading: "Livestock Trailer Covers", text: "Shade and weather protection that doesn't spook the cattle. Roll-up panels for ventilation." },
        { heading: "Stock Trailer Tarps", text: "Heavy-duty replacement tarps for any trailer size — patterned and installed at our shop or yours." },
        { heading: "Ranch Equipment Covers", text: "Tractor canopies, hay tarps, and custom equipment shrouds built for the working ranch." },
      ]}
      applications={["Livestock Trailers", "Cattle Haulers", "Horse Trailers", "Hay Tarps", "Tractor Canopies", "Equipment Sheds"]}
      faqs={[
        { q: "Will a custom stock trailer cover fit my trailer?", a: "Yes — every cover we build is patterned to your specific trailer, whether it's a stock combo, gooseneck, bumper-pull, or a one-off ranch build. Bring it to our Seguin shop or we'll come pattern it on-site." },
        { q: "What fabric do you use for ag tarps?", a: "Heavy 18oz vinyl-coated polyester for most ranch tarps and stock trailer covers — UV-stabilized, mildew-resistant, and rated for working ranch use." },
        { q: "Can you replace just the tarp on my existing frame?", a: "Yes. If your frame is sound, we can pattern and sew a replacement tarp at a fraction of the cost of a complete cover." },
        { q: "Do you build covers for ranch equipment beyond trailers?", a: "Tractor canopies, hay tarps, equipment shrouds, ATV/UTV covers, and one-off ranch builds — if it needs a tough, custom-fit cover, we can build it." },
      ]}
      gallerySection={{
        eyebrow: "Stock Trailer & Ag Tarp Projects",
        title: "Recent Ag Tarp & Stock Trailer Builds",
        intro: "A look at recent custom ag tarps, stock trailer covers, and ranch equipment shrouds — patterned, sewn, and installed by our Seguin crew for working Texas ranches. Tap any photo to jump to that cover type.",
        subsections: [
          {
            id: "livestock-trailer-covers",
            heading: "Livestock Trailer Covers — 18oz Vinyl Tops",
            body: "Custom-patterned 18oz vinyl-coated polyester top covers for bumper-pull livestock trailers. Reinforced corner stress points, double-stitched seams, and a tight fit that doesn't spook cattle on the highway. Built to shed Central Texas rain and shade animals on long hauls.",
          },
          {
            id: "gooseneck-stock-trailer-tops",
            heading: "Gooseneck Stock Trailer Tops — White Reflective Vinyl",
            body: "Heavy white 18oz vinyl tops patterned to gooseneck cattle trailers up to 32 feet. White face reflects Texas sun to keep livestock cooler; UV- and mildew-resistant for daily ranch use, with stainless grommets and webbing tie-downs.",
          },
          {
            id: "hay-trailer-tarps",
            heading: "Hay Trailer Tarps & Round Bale Covers",
            body: "Heavy 18oz white vinyl tarps sized to flat-deck hay trailers and round-bale rigs. Sheds rain off bales and protects feed inventory from sun bleaching between cuttings. Reinforced D-rings on 24\" centers for fast tie-down.",
          },
        ],
        columns: 3,
        images: [
          {
            src: livestockBlack,
            alt: "Custom black 18oz vinyl-coated polyester top cover patterned to fit a 24-foot bumper-pull livestock stock trailer in Seguin, TX",
            label: "Bumper-Pull Livestock Trailer · 18oz Black Vinyl Top",
            caption: "Heavy 18oz vinyl-coated polyester top cover, patterned to a 24' bumper-pull livestock trailer with reinforced corner stress points and double-stitched seams — built to shade cattle and shed Central Texas rain on long hauls.",
            linkHref: "#livestock-trailer-covers",
            linkLabel: "Livestock Trailer Covers",
          },
          {
            src: redGooseneck,
            alt: "Red 28-foot gooseneck stock trailer fitted with a heavy white 18oz vinyl top tarp built for working Texas ranch use",
            label: "28' Gooseneck Stock Trailer · White 18oz Vinyl Top",
            caption: "Custom-patterned white 18oz vinyl top for a 28' red gooseneck stock trailer. White face reflects sun to keep livestock cooler; UV- and mildew-resistant for daily ranch use.",
            linkHref: "#gooseneck-stock-trailer-tops",
            linkLabel: "Gooseneck Stock Trailer Tops",
          },
          {
            src: redGooseneckInstalled,
            alt: "Installed custom-fit white 18oz vinyl top cover stretched tight over the rails of a red gooseneck cattle trailer for a Central Texas ranch",
            label: "Gooseneck Cattle Trailer · Installed Top Cover",
            caption: "Same gooseneck after install — white 18oz vinyl pulled tight over the top rails with stainless grommets and webbing tie-downs, ready for hauling cattle in summer heat.",
            linkHref: "#gooseneck-stock-trailer-tops",
            linkLabel: "Gooseneck Stock Trailer Tops",
          },
          {
            src: blueStockTanTarp,
            alt: "Bright blue bumper-pull livestock stock trailer fitted with a custom tan heavy-duty vinyl top tarp patterned, sewn and installed by Texas Made Shade in Seguin, Texas",
            label: "Blue Bumper-Pull Stock Trailer · Tan Vinyl Top",
            caption: "Custom tan 18oz vinyl top patterned to a bright-blue bumper-pull livestock trailer — laced down through brass grommets for a tight, weather-shedding fit on Central Texas ranch hauls.",
            linkHref: "#livestock-trailer-covers",
            linkLabel: "Livestock Trailer Covers",
          },
        ],
        cta: {
          headline: "Get a Free Quote on Your Custom Trailer Cover or Ag Tarp",
          subhead: "Tell us your trailer make and length (or send a couple photos) and we'll come back same business day with sizing questions and a no-pressure estimate. Patterned in Seguin, installed across Texas.",
          buttonLabel: "Get My Free Quote",
          href: `/#lets-talk?type=${encodeURIComponent("Stock Trailer Cover / Ag Tarp")}&notes=${encodeURIComponent("Trailer cover / ag tarp inquiry — please share trailer make, length, deck width, whether you need a top only or top + side panels, and the primary use case (cattle, horse, hay, equipment).")}`,
          phoneNumber: "+1 (830) 390-9588",
        },
      }}
      projectHighlights={{
        eyebrow: "Recent Trailer Builds",
        title: "Featured Ag Tarp & Stock Trailer Builds",
        intro: "Three recent custom builds out of the Seguin shop — patterned to the trailer, sewn in-house, and installed by the same crew. Each cover is spec'd for its working environment, not pulled off a shelf.",
        items: [
          {
            title: "28' Gooseneck Cattle Trailer Top",
            description: "White 18oz vinyl top cover patterned to a 28-foot red gooseneck cattle trailer for a Central Texas ranch — built to keep livestock cool on long summer hauls.",
            specs: [
              "18oz vinyl-coated polyester (white)",
              "Patterned to 28' deck × 7' wide",
              "Stainless grommets · webbing tie-downs",
              "Double-stitched seams · reinforced corners",
              "Use case: daily cattle haul, summer sun",
            ],
          },
          {
            title: "20' Hay Trailer Tarp",
            description: "Heavy 18oz white vinyl tarp sized for a 20' flat-deck hay trailer — sheds rain off round bales and protects feed inventory between cuttings.",
            specs: [
              "18oz vinyl-coated polyester (white)",
              "20' × 8' finished tarp size",
              "Reinforced D-rings on 24\" centers",
              "UV-stabilized · mildew-resistant",
              "Use case: hay storage + over-the-road",
            ],
          },
        ],
      }}
      testimonials={{
        eyebrow: "Trailer Cover Customer Reviews · 5.0 Rating",
        title: "What Texas Ranchers Say About Their Trailer Covers",
        items: [
          {
            quote:
              "Texas Made Shade patterned an 18oz vinyl top for our 28' gooseneck cattle trailer and it fits like it was poured on. Three summers in and it still looks new — cattle are noticeably cooler on long hauls. Worth every penny.",
            author: "Wade B.",
            role: "28' Gooseneck Cattle Trailer Top",
            location: "Floresville, TX",
            rating: 5,
          },
          {
            quote:
              "Custom hay tarp on our 20' flat-deck has paid for itself twice over already. No more weather-rotted bales between cuttings. Fast turnaround out of the Seguin shop and a fair price.",
            author: "Cody R.",
            role: "20' Hay Trailer Tarp",
            location: "Seguin, TX",
            rating: 5,
          },
        ],
      }}
    />
  ),
});
